import * as React from 'react';
import { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Lightning as LightningIcon,
  Plus as PlusIcon,
  Timer as TimerIcon,
  Users as UsersIcon,
  XCircle as XCircleIcon,
} from '@phosphor-icons/react';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import type { Amenity } from '@/types/v2/amenity';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { useUser } from '@/hooks/v2/use-user';
import { paths } from '@/paths';
import { hasAnyAccountRoles, hasCommunityRolesV2 } from '@/utils/has-roles';

import { CreateAmenityDialog } from './create-amenity-dialog';

function formatDuration(minutes: number): string {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    const d = minutes / 1440;
    return d === 1 ? '1 day' : `${d} days`;
  }
  if (minutes >= 60 && minutes % 60 === 0) {
    const h = minutes / 60;
    return h === 1 ? '1 hr' : `${h} hrs`;
  }
  return `${minutes} min`;
}

interface AmenitiesTableProps {
  rows: Amenity[];
  loading: boolean;
  onCreated: (amenity: Amenity) => void;
}

export function AmenitiesTable({ rows, loading, onCreated }: AmenitiesTableProps): React.JSX.Element {
  const navigate = useNavigate();
  const { account, community } = useUser();

  const [openCreate, setOpenCreate] = React.useState(false);

  const columns = useMemo<MRT_ColumnDef<Amenity>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Amenity',
        size: 280,
        grow: true,
        enableSorting: true,
        Cell: ({ row }) => {
          const amenity = row.original;
          return (
            <Box>
              <Typography fontWeight={600} variant="body2" sx={{ mb: 0.25 }}>
                {amenity.name}
              </Typography>
              {amenity.description && (
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}
                >
                  {amenity.description}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: 'capacity',
        header: 'Capacity',
        size: 100,
        enableSorting: true,
        Cell: ({ row }) => {
          const cap = row.original.capacity;
          return cap ? (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <UsersIcon size={14} />
              <Typography variant="body2">{cap}</Typography>
            </Stack>
          ) : (
            <Typography color="text.secondary" variant="body2">—</Typography>
          );
        },
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        size: 110,
        enableColumnFilter: true,
        filterVariant: 'select',
        filterSelectOptions: [
          { text: 'Active', value: 'true' },
          { text: 'Inactive', value: 'false' },
        ],
        Cell: ({ row }) => (
          <Chip
            icon={row.original.is_active ? <CheckCircleIcon size={14} /> : <XCircleIcon size={14} />}
            label={row.original.is_active ? 'Active' : 'Inactive'}
            size="small"
            color={row.original.is_active ? 'success' : 'default'}
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        accessorKey: 'requires_approval',
        header: 'Approval',
        size: 110,
        Cell: ({ row }) => (
          <Chip
            label={row.original.requires_approval ? 'Required' : 'Auto'}
            size="small"
            color={row.original.requires_approval ? 'warning' : 'info'}
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        id: 'duration',
        header: 'Duration',
        size: 160,
        Cell: ({ row }) => {
          const a = row.original;
          const block = a.time_block_duration;
          const min = a.reservation_min_duration;
          const max = a.reservation_max_duration;

          if (block && block > 0) {
            return (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <LightningIcon size={14} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDuration(block)} block</Typography>
              </Stack>
            );
          }

          const hasMin = min && min > 0;
          const hasMax = max && max > 0;

          if (!hasMin && !hasMax) {
            return <Typography color="text.secondary" variant="body2">Flexible</Typography>;
          }

          return (
            <Stack spacing={0.25}>
              {hasMin && (
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                  <TimerIcon size={13} />
                  <Typography variant="caption">Min {formatDuration(min)}</Typography>
                </Stack>
              )}
              {hasMax && (
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                  <TimerIcon size={13} />
                  <Typography variant="caption">Max {formatDuration(max)}</Typography>
                </Stack>
              )}
            </Stack>
          );
        },
      },
      {
        id: 'fees',
        header: 'Fees',
        size: 140,
        Cell: ({ row }) => {
          const deposit = row.original.deposit_amount;
          const total = row.original.total_fee_amount;
          const hasDeposit = deposit != null && Number(deposit) > 0;
          const hasTotal = total != null && Number(total) > 0;

          if (!hasDeposit && !hasTotal) {
            return <Typography color="text.secondary" variant="body2">Free</Typography>;
          }

          return (
            <Box>
              {hasTotal && (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  ${Number(total).toFixed(2)}
                </Typography>
              )}
              {hasDeposit && (
                <Typography variant="caption" color="text.secondary">
                  ${Number(deposit).toFixed(2)} deposit
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        size: 120,
        enableSorting: true,
        Cell: ({ cell }) => (
          <Typography variant="body2">
            {dayjs(cell.getValue<string>()).format('MMM D, YYYY')}
          </Typography>
        ),
      },
    ],
    [],
  );

  const activeCount = rows.filter((a) => a.is_active).length;
  const inactiveCount = rows.length - activeCount;
  const canCreate = hasCommunityRolesV2(community, [
    COMMUNITY_ROLES.COMMUNITY_MANAGER,
    COMMUNITY_ROLES.BOARD_MEMBER,
  ]) || hasAnyAccountRoles(account!, [
    ACCOUNT_ROLES.ACCOUNT_OWNER,
    ACCOUNT_ROLES.ACCOUNT_ADMIN,
    ACCOUNT_ROLES.ACCOUNT_MANAGER,
  ]);

  const table = useMaterialReactTable<Amenity>({
    columns,
    data: rows,
    initialState: {
      showGlobalFilter: true,
      showColumnFilters: false,
      sorting: [{ id: 'name', desc: false }],
      density: 'compact',
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
      placeholder: 'Search amenities...',
    },
    state: { isLoading: loading },
    renderTopToolbar: ({ table }) => (
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          px: 2,
          py: 1.5,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <MRT_GlobalFilterTextField table={table} />
          <MRT_ToggleFiltersButton table={table} />
          <MRT_ShowHideColumnsButton table={table} />
          <MRT_ToggleDensePaddingButton table={table} />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              ml: 1,
              pl: 1.5,
              borderLeft: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography color="text.disabled" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {activeCount} active
            </Typography>
            <Typography color="text.disabled" sx={{ fontSize: '0.6875rem' }}>·</Typography>
            <Typography color="text.disabled" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {inactiveCount} inactive
            </Typography>
          </Box>
        </Stack>

        {canCreate ? (
          <Button
            startIcon={<PlusIcon size={16} />}
            variant="contained"
            onClick={() => setOpenCreate(true)}
            size="small"
            sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}
          >
            Add Amenity
          </Button>
        ) : null}
      </Box>
    ),
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', borderRadius: 0 } },
    enablePagination: true,
    enableStickyHeader: true,
    muiTableProps: { sx: { tableLayout: 'fixed' } },
    muiTableHeadProps: {
      sx: {
        '& tr th': {
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.secondary',
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          py: 1.5,
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        '& tr td': {
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: '0.875rem',
          py: 1.5,
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        const id = row.original.uuid ?? row.original.id;
        navigate(paths.settings.community.amenities.amenity(id));
      },
      sx: {
        cursor: 'pointer',
        '&:hover td': { bgcolor: 'action.hover' },
      },
    }),
  });

  return (
    <>
      <MaterialReactTable table={table} />

      {canCreate ? (
        <CreateAmenityDialog open={openCreate} onClose={() => setOpenCreate(false)} onSuccess={onCreated} />
      ) : null}
    </>
  );
}
